import { formatSalesOrderNumber } from '@order-management/format';
import {
  isEmailInDomains,
  PermissionsGroupName,
} from '@order-management/roles';
import { NewUserAccessPermissionsGroupUserRoleAssignment } from '@order-management/types';
import { useCallback, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import { environment } from '../../../environments/environment';
import Conditional from '../../shared/conditional/conditional';
import If from '../../shared/if/if';
import Button from '../../ui/button/button';
import Modal from '../../ui/modal/modal';

export enum UserAccessSetupStatus {
  ASSIGNMENTS_ERROR = 'ASSIGNMENTS ERROR',
  DOMAIN_ERROR = 'DOMAIN ERROR',
  CONFIRMATION_REQUIRED = 'CONFIRMATION REQUIRED',
  COMPLETED = 'COMPLETED',
}

export type CustomerAccessSetupConfirmationModalProps = {
  show: boolean;
  email: string;
  assignmentsSalesOrdersNumbers: string[];
  conflictingAssignments?: NewUserAccessPermissionsGroupUserRoleAssignment[];
  onAddAnotherUser: () => void;
  onConfirm: () => void;
  onClose: () => void;
  navigateToTemporaryAccessDashboardPage: () => void;
  navigateToTemporaryAccessSetupPage: () => void;
  loading: boolean;
};

export function CustomerAccessSetupConfirmationModal({
  show,
  email,
  assignmentsSalesOrdersNumbers,
  conflictingAssignments,
  onAddAnotherUser,
  onConfirm,
  onClose,
  navigateToTemporaryAccessDashboardPage,
  navigateToTemporaryAccessSetupPage,
  loading,
}: CustomerAccessSetupConfirmationModalProps) {
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);

  const allowedInternalAndPartnerEmailDomains = useMemo(
    () =>
      environment.internalUserEmailDomains
        .split(',')
        .concat(environment.partnerUserEmailDomains.split(',')),
    []
  );

  const conflictingAssignmentsData = useMemo(() => {
    const data: {
      overridablePermissionsGroupAssignments: Map<
        PermissionsGroupName,
        Set<string>
      >;
      nonOverridablePermissionsGroupAssignments: Map<
        PermissionsGroupName,
        Set<string>
      >;
    } = {
      overridablePermissionsGroupAssignments: new Map(),
      nonOverridablePermissionsGroupAssignments: new Map(),
    };
    if (!conflictingAssignments) return data;
    for (const assignment of conflictingAssignments) {
      const salesOrder = formatSalesOrderNumber(assignment.salesOrder.number);
      const key = assignment.permissionsGroup.name as PermissionsGroupName;
      if (assignment.overridable) {
        const permissionGroup =
          data.overridablePermissionsGroupAssignments.get(key);
        if (permissionGroup) {
          permissionGroup.add(salesOrder);
        } else {
          data.overridablePermissionsGroupAssignments.set(
            key,
            new Set([salesOrder])
          );
        }
      } else {
        const permissionGroup =
          data.nonOverridablePermissionsGroupAssignments.get(key);
        if (permissionGroup) {
          permissionGroup.add(salesOrder);
        } else {
          data.nonOverridablePermissionsGroupAssignments.set(
            key,
            new Set([salesOrder])
          );
        }
      }
    }
    return data;
  }, [conflictingAssignments]);

  const status: UserAccessSetupStatus = useMemo(() => {
    const isInvalidEmailDomain = isEmailInDomains(
      email,
      allowedInternalAndPartnerEmailDomains
    );
    if (isInvalidEmailDomain) {
      return UserAccessSetupStatus.DOMAIN_ERROR;
    }
    if (!conflictingAssignments?.length || (overrideConfirmed && !loading)) {
      return UserAccessSetupStatus.COMPLETED;
    }
    if (!conflictingAssignments?.some((assignment) => assignment.overridable)) {
      return UserAccessSetupStatus.ASSIGNMENTS_ERROR;
    }
    return UserAccessSetupStatus.CONFIRMATION_REQUIRED;
  }, [
    allowedInternalAndPartnerEmailDomains,
    conflictingAssignments,
    email,
    loading,
    overrideConfirmed,
  ]);

  const title = useMemo(() => {
    switch (status) {
      case UserAccessSetupStatus.CONFIRMATION_REQUIRED: {
        return 'Are you sure?';
      }
      case UserAccessSetupStatus.COMPLETED: {
        return 'Access granted';
      }
      case UserAccessSetupStatus.ASSIGNMENTS_ERROR:
      case UserAccessSetupStatus.DOMAIN_ERROR: {
        return 'Error';
      }
      default: {
        return '';
      }
    }
  }, [status]);

  const handleConfirmOverride = useCallback(() => {
    setOverrideConfirmed(true);
    onConfirm();
  }, [onConfirm]);

  const handleAddAnotherUser = useCallback(() => {
    setOverrideConfirmed(false);
    onAddAnotherUser();
  }, [onAddAnotherUser]);

  const ConflictingAssignmentsList = useCallback(
    ({
      assignmentsMap,
    }: {
      assignmentsMap: Map<PermissionsGroupName, Set<string>>;
    }) => {
      return (
        <Element>
          {Array.from(assignmentsMap.entries()).map(
            ([permissionGroupName, salesOrders]) => (
              <p key={permissionGroupName}>
                <span className="has-text-weight-bold">
                  {Array.from(salesOrders).join(', ')}
                </span>
                <span> - {permissionGroupName}</span>
              </p>
            )
          )}
        </Element>
      );
    },
    []
  );

  const Content = useCallback(() => {
    switch (status) {
      case UserAccessSetupStatus.CONFIRMATION_REQUIRED: {
        return (
          <Element display="flex" flexDirection="column" className="gap-6">
            <If
              condition={
                conflictingAssignmentsData
                  .nonOverridablePermissionsGroupAssignments.size > 0
              }
            >
              <>
                <p>
                  The user you’re trying to assign as Manager already has the
                  following roles that can’t be overridden:
                </p>
                <ConflictingAssignmentsList
                  assignmentsMap={
                    conflictingAssignmentsData.nonOverridablePermissionsGroupAssignments
                  }
                />
                <p>
                  <span>The Manager role </span>
                  <span className="has-text-danger-dark has-text-weight-bold">
                    will not
                  </span>
                  <span> be applied to these Sales Orders.</span>
                </p>
              </>
            </If>
            <If
              condition={
                conflictingAssignmentsData
                  .overridablePermissionsGroupAssignments.size > 0
              }
            >
              <>
                <Conditional
                  condition={
                    conflictingAssignmentsData
                      .nonOverridablePermissionsGroupAssignments.size > 0
                  }
                >
                  <Conditional.True>
                    <p>
                      Also, they already have the following roles that can be
                      overridden:
                    </p>
                  </Conditional.True>
                  <Conditional.False>
                    <p>
                      The user you’re trying to assign as Manager already has
                      the following roles that can be overridden:
                    </p>
                  </Conditional.False>
                </Conditional>

                <ConflictingAssignmentsList
                  assignmentsMap={
                    conflictingAssignmentsData.overridablePermissionsGroupAssignments
                  }
                />
                <p>Do you want to override these assignments?</p>
              </>
            </If>
          </Element>
        );
      }
      case UserAccessSetupStatus.COMPLETED: {
        return (
          <p>
            <span>You have successfully assigned {email} as Manager for </span>
            <span className="has-text-weight-bold">
              {assignmentsSalesOrdersNumbers.join(', ')}
            </span>
          </p>
        );
      }
      case UserAccessSetupStatus.ASSIGNMENTS_ERROR: {
        return (
          <Element display="flex" flexDirection="column" className="gap-4">
            <p>
              The user you’re trying to assign as Manager already has the
              following roles that can’t be overridden:
            </p>
            <ConflictingAssignmentsList
              assignmentsMap={
                conflictingAssignmentsData.nonOverridablePermissionsGroupAssignments
              }
            />
            <p>Please go back and change your selection.</p>
          </Element>
        );
      }
      case UserAccessSetupStatus.DOMAIN_ERROR: {
        return (
          <Element display="flex" flexDirection="column" className="gap-4">
            <p>
              <span>
                You are trying to assign a zSpace account as Manager for{' '}
              </span>
              <span className="has-text-weight-bold">
                {assignmentsSalesOrdersNumbers.join(', ')}
              </span>
            </p>
            <p>
              To assign zSpace users to customer Sales Orders, please use the
              “Grant Temporary Access” process.
            </p>
          </Element>
        );
      }
      default:
        return;
    }
  }, [
    ConflictingAssignmentsList,
    assignmentsSalesOrdersNumbers,
    conflictingAssignmentsData.nonOverridablePermissionsGroupAssignments,
    conflictingAssignmentsData.overridablePermissionsGroupAssignments,
    email,
    status,
  ]);

  const ActionsButtons = useCallback(() => {
    switch (status) {
      case UserAccessSetupStatus.CONFIRMATION_REQUIRED: {
        return (
          <>
            <Button color="danger-dark" outlined onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="primary-dark"
              onClick={handleConfirmOverride}
              loading={loading}
            >
              Confirm
            </Button>
          </>
        );
      }
      case UserAccessSetupStatus.COMPLETED: {
        return (
          <>
            <Button color="primary-dark" onClick={handleAddAnotherUser}>
              Add another user to the same SO
            </Button>
            <Button
              color="primary-dark"
              onClick={navigateToTemporaryAccessDashboardPage}
            >
              Done
            </Button>
          </>
        );
      }
      case UserAccessSetupStatus.ASSIGNMENTS_ERROR: {
        return (
          <Button color="danger-dark" outlined onClick={onClose}>
            Go back
          </Button>
        );
      }
      case UserAccessSetupStatus.DOMAIN_ERROR: {
        return (
          <>
            <Button color="danger-dark" outlined onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="primary-dark"
              onClick={navigateToTemporaryAccessSetupPage}
            >
              Grant temporary access
            </Button>
          </>
        );
      }
    }
  }, [
    handleAddAnotherUser,
    handleConfirmOverride,
    loading,
    navigateToTemporaryAccessDashboardPage,
    navigateToTemporaryAccessSetupPage,
    onClose,
    status,
  ]);

  return (
    <Modal show={show} title={title} titleClassName="mb-4">
      <Content />
      <Element
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mt={8}
      >
        <ActionsButtons />
      </Element>
    </Modal>
  );
}

export default CustomerAccessSetupConfirmationModal;
